import { bilinear, inverseBilinear } from "../native/bilinear";
import { edgePatch, hasEdgeWarp } from "../geometry/edge-warp";
import type { Point, SceneNode, SceneAsset } from "../scene/model";
import type { ImageStore } from "./image-store";
import { invertAffine, transformPoint } from "../geometry/affine";
import { nodeMatrix } from "../native/transforms";
import { inversePerspective } from "../native/deformation";
/** Background picking samples original pixels, before hue/alpha/edge processing. */
export function sampleOriginalImage(
  images: ImageStore,
  node: SceneNode,
  assets: readonly SceneAsset[],
  world: Point,
) {
  const inverse = invertAffine(nodeMatrix(node));
  if (!inverse) return null;
  const p = transformPoint(world, inverse);
  let uv = node.geometry.warpCorners
    ? node.geometry.warpMapping === "bilinear"
      ? inverseBilinear(node.geometry.warpCorners)(p.x, p.y)
      : inversePerspective(node.geometry.warpCorners, p)
    : { x: p.x + 0.5, y: p.y + 0.5 };
  if (node.geometry.warpCorners && hasEdgeWarp(node.geometry.warpEdges)) {
    const local = edgePatch(
      node.geometry.warpCorners,
      node.geometry.warpEdges!,
    ).inverse(p.x, p.y);
    if (!local) return null;
    uv =
      node.geometry.warpMapping === "bilinear"
        ? local
        : inversePerspective(
            node.geometry.warpCorners,
            bilinear(node.geometry.warpCorners, local.x, local.y),
          );
  }
  if (uv.x < 0 || uv.y < 0 || uv.x >= 1 || uv.y >= 1) return null;
  const image = images.get(
    assets.find((a) => a.id === node.media.image)?.source ?? "",
  );
  if (!image) return null;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(
    image,
    Math.floor(uv.x * image.naturalWidth),
    Math.floor(uv.y * image.naturalHeight),
    1,
    1,
    0,
    0,
    1,
    1,
  );
  const data = ctx.getImageData(0, 0, 1, 1).data;
  return data[3]
    ? "#" +
        [data[0]!, data[1]!, data[2]!]
          .map((n) => n.toString(16).padStart(2, "0"))
          .join("")
    : null;
}
